import React, { useState, useEffect } from "react";
import geoData from "../Data/GeoCodes"; // Asegúrate de importar el archivo geoData.js
import PageLayout from "../components/layout/PageLayout";
import { LoadingOverlay } from "../components/ui/loading-overlay";
import { useGoogleLogin } from "@react-oauth/google";
import { useMsal } from "@azure/msal-react"; // 🔹 Correcto uso de MSAL
import { useCompany } from "../Context/CompanyContext";
import { useUser } from "../Context/UserContext";
import { useNavigate, Link } from "react-router-dom";
import { Eye, EyeOff } from "lucide-react";
import API_URL from "../config";
import { Helmet } from "react-helmet-async";
import { Card, CardContent } from "../components/ui/card";
import { Input } from "../components/ui/input";
import { Button } from "../components/ui/button";

const Register = () => {
  const { user, fetchUserInfo } = useUser(); // Carga de la info básica del usuario
  const navigate = useNavigate();
  const { fetchCompanies } = useCompany(); // Carga de empresas asociadas
  const { instance } = useMsal(); // 🔹 Obtener la instancia de MSAL

  const [nombre, setNombre] = useState("");
  const [apellido, setApellido] = useState("");
  const [codigoPais, setCodigoPais] = useState(geoData[0].dialCode);
  const [celular, setCelular] = useState("");
  const [correo, setCorreo] = useState("");
  const [contrasena, setContrasena] = useState("");
  const [repetirContrasena, setRepetirContrasena] = useState("");
  const [error, setError] = useState("");
  const [isLoading, setIsLoading] = useState(false);

  const [showPassword, setShowPassword] = useState({
    contrasena: false,
    repetirContrasena: false,
  });

  // Redirigir al menú si ya está logueado
  useEffect(() => {
    if (user) {
      navigate(
        `/menu/${user.user?.id_user}/${user.user?.nombre}-${user.user?.apellido}`
      );
    }
  }, [user, navigate]);

  const validatePhoneNumber = (celular) => {
    if (!/^\d{9,}$/.test(celular)) {
      return "El número de celular debe tener al menos 9 dígitos numéricos";
    }

    return null; // Si no hay errores, retorna null
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setIsLoading(true); // Mostrar modal de carga

    if (contrasena !== repetirContrasena) {
      setError("Las contraseñas no coinciden");
      setIsLoading(false); // Ocultar modal

      return;
    }

    const celularError = validatePhoneNumber(celular);
    if (celularError) {
      setError(celularError);
      setIsLoading(false);

      return;
    }

    try {
      const registerResponse = await fetch(`${API_URL}/user/register`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          nombre,
          apellido,
          codigoPais,
          celular,
          correo,
          contrasena,
          anon_chat_id: localStorage.getItem("procu_anon_chat_id"),
        }),
      });

      const registerData = await registerResponse.json();

      if (!registerResponse.ok) {
        setError(registerData.error);
        setIsLoading(false);
        return;
      }

      // Si el registro es exitoso, proceder a iniciar sesión automáticamente
      const loginResponse = await fetch(`${API_URL}/user/login`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ correo, contrasena }),
      });

      const loginData = await loginResponse.json();

      if (!loginResponse.ok) {
        setError("Error al iniciar sesión después del registro");
        setIsLoading(false);
        return;
      }

      // Guardar tokens en localStorage
      localStorage.setItem(
        `access_token_${loginData.id_user}`,
        loginData.access_token
      );
      localStorage.setItem(
        `refresh_token_${loginData.id_user}`,
        loginData.refresh_token
      );
      localStorage.setItem("current_user_id", loginData.id_user);

      // Cargar información del usuario y redirigirlo al menú
      await fetchUserInfo();
      navigate(
        `/menu/${loginData.id_user}/${loginData.nombre}-${loginData.apellido}`
      );
    } catch (error) {
      setError("Error en la solicitud");
    } finally {
      setIsLoading(false); // Ocultar modal al finalizar
    }
  };

  const toggleShowPassword = (field) => {
    setShowPassword((prevState) => ({
      ...prevState,
      [field]: !prevState[field],
    }));
  };

  const handleOAuthLogin = async (provider, token) => {
    setIsLoading(true);
    try {
      const res = await fetch(`${API_URL}/user/${provider}/callback`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ token }),
      });

      if (!res.ok) {
        throw new Error(`Error en la autenticación con ${provider}`);
      }

      const data = await res.json();
      if (data.access_token) {
        localStorage.setItem(`access_token_${data.id_user}`, data.access_token);
        localStorage.setItem(
          `refresh_token_${data.id_user}`,
          data.refresh_token
        );
        localStorage.setItem("current_user_id", data.id_user);

        // Cargar información del usuario
        await fetchUserInfo();
        fetchCompanies();

        // Redirigir al usuario al menú
        navigate(`/menu/${data.id_user}/${data.nombre}-${data.apellido}`);
      }
    } catch (error) {
      console.error(`Error en el login con ${provider}:`, error);
      setError(`Error en la autenticación con ${provider}`);
    } finally {
      setIsLoading(false);
    }
  };

  const handleMicrosoftLogin = async () => {
    try {
      const loginResponse = await instance.loginPopup({
        scopes: ["openid", "profile", "email"],
        prompt: "select_account",
      });

      console.log("Microsoft loginResponse:", loginResponse);

      if (!loginResponse.idToken) {
        throw new Error("No se obtuvo idToken de Microsoft");
      }

      // Enviar idToken al backend
      await handleOAuthLogin("microsoft", loginResponse.idToken);
    } catch (error) {
      console.error("Error en la autenticación con Microsoft:", error);
      setError("Error en la autenticación con Microsoft");
    }
  };

  const handleGoogleLogin = async (tokenResponse) => {
    setIsLoading(true); // Mostrar modal de carga
    console.log("Google login success:", tokenResponse);

    try {
      const res = await fetch(`${API_URL}/user/google/callback`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ token: tokenResponse.code }), //✅ Ahora usas correctamente el código de autorización
      });

      if (!res.ok) {
        throw new Error(`Error en la solicitud: ${res.statusText}`);
      }

      const data = await res.json();
      console.log("Respuesta del backend:", data);

      if (data.id_user) {
        const userId = String(data.id_user);

        // Guardar tokens en localStorage
        localStorage.setItem(`access_token_${userId}`, data.access_token);
        localStorage.setItem(`refresh_token_${userId}`, data.refresh_token);
        localStorage.setItem("current_user_id", userId);

        console.log("✅ Tokens guardados en localStorage");

        // 🔹 Asegurarnos de cargar la información del usuario ANTES de redirigir
        await fetchUserInfo();
        fetchCompanies();

        console.log("✅ Información del usuario y empresas cargada");

        // 🔹 Ahora sí redirigir
        navigate(`/menu/${data.id_user}/${data.nombre}-${data.apellido}`);
      } else {
        console.error("Error: No se recibió un token de acceso válido", data);
      }
    } catch (error) {
      console.error("Error en el login con Google", error);
    } finally {
      setIsLoading(false); // Ocultar modal de carga
    }
  };

  const loginWithGoogle = useGoogleLogin({
    onSuccess: handleGoogleLogin,
    onError: () => console.log("Error en la autenticación con Google"),
    flow: "auth-code", // 🔥 Cambia a 'auth-code' para obtener un JWT id_token
  });

  return (
    <PageLayout>
      <Helmet>
        <title>Registrarse - Procu</title>
        <meta
          name="description"
          content="Crea una cuenta en Procu y accede a la mejor plataforma para conectar empresas con proveedores. Encuentra licitaciones y oportunidades de negocio fácilmente."
        />
        <meta name="keywords" content="registrarse, crear cuenta, Procu, proveedores, licitaciones, negocios, B2B" />
      </Helmet>

      {isLoading && <LoadingOverlay />}

      <div className="flex items-center justify-center px-4 py-16">
        <Card className="w-full max-w-lg">
          <CardContent className="pt-6">
            <h2 className="text-center text-2xl font-semibold text-foreground">Regístrate</h2>

            <div className="mt-6 flex flex-col gap-2">
              <Button
                type="button"
                variant="outline"
                className="w-full"
                onClick={() => loginWithGoogle()}
              >
                <img src="/fotos/google_logo.png" alt="Google" className="h-5 w-5" />
                Iniciar sesión con Google
              </Button>
              <Button
                type="button"
                variant="outline"
                className="w-full"
                onClick={handleMicrosoftLogin}
              >
                <img src="/fotos/microsoft_logo.svg" alt="Microsoft" className="h-5 w-5" />
                Iniciar sesión con Microsoft
              </Button>
            </div>

            <div className="my-6 flex items-center gap-3">
              <div className="h-px flex-1 bg-border" />
              <span className="text-sm text-muted-foreground">O</span>
              <div className="h-px flex-1 bg-border" />
            </div>

            <form onSubmit={handleSubmit} className="flex flex-col gap-4">
              <div>
                <label htmlFor="nombre" className="mb-1.5 block text-sm font-medium text-foreground">
                  Nombre
                </label>
                <Input
                  type="text"
                  id="nombre"
                  value={nombre}
                  onChange={(e) => setNombre(e.target.value)}
                  placeholder="Ejemplo: 'Nombre'"
                  maxLength="50"
                  pattern="^[a-zA-ZáéíóúÁÉÍÓÚñÑ\s]+$"
                  title="El nombre solo debe contener letras y espacios"
                  required
                />
              </div>

              <div>
                <label htmlFor="apellido" className="mb-1.5 block text-sm font-medium text-foreground">
                  Apellido
                </label>
                <Input
                  type="text"
                  id="apellido"
                  value={apellido}
                  onChange={(e) => setApellido(e.target.value)}
                  placeholder="Ejemplo: 'Apellido Apellido'"
                  maxLength="50"
                  pattern="^[a-zA-ZáéíóúÁÉÍÓÚñÑ\s]+$"
                  title="El apellido solo debe contener letras y espacios"
                  required
                />
              </div>

              <div>
                <label htmlFor="celular" className="mb-1.5 block text-sm font-medium text-foreground">
                  Celular
                </label>
                <div className="flex gap-2">
                  <select
                    id="codigo_pais"
                    value={codigoPais}
                    onChange={(e) => setCodigoPais(e.target.value)}
                    required
                    className="rounded-md border border-border bg-background px-2 text-sm text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary"
                  >
                    {geoData.map((country) => (
                      <option key={country.code} value={country.dialCode}>
                        {country.code} - {country.dialCode}
                      </option>
                    ))}
                  </select>
                  <Input
                    type="text"
                    id="celular"
                    value={celular}
                    onChange={(e) => setCelular(e.target.value)}
                    required
                    pattern="^\d{9,15}$"
                    maxLength="15"
                    title="El número de celular debe tener entre 9 y 15 dígitos numéricos"
                    placeholder="Número de celular"
                  />
                </div>
              </div>

              <div>
                <label htmlFor="correo" className="mb-1.5 block text-sm font-medium text-foreground">
                  Correo Electrónico
                </label>
                <Input
                  type="email"
                  id="correo"
                  value={correo}
                  onChange={(e) => setCorreo(e.target.value)}
                  maxLength="80"
                  required
                />
              </div>

              <div>
                <label htmlFor="contrasena" className="mb-1.5 block text-sm font-medium text-foreground">
                  Contraseña
                </label>
                <div className="relative">
                  <Input
                    type={showPassword.contrasena ? "text" : "password"}
                    id="contrasena"
                    value={contrasena}
                    onChange={(e) => setContrasena(e.target.value)}
                    required
                    minLength="6"
                    maxLength="25"
                    className="pr-10"
                  />
                  <button
                    type="button"
                    onClick={() => toggleShowPassword("contrasena")}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                  >
                    {showPassword.contrasena ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                  </button>
                </div>
              </div>

              <div>
                <label htmlFor="repetirContrasena" className="mb-1.5 block text-sm font-medium text-foreground">
                  Repetir Contraseña
                </label>
                <div className="relative">
                  <Input
                    type={showPassword.repetirContrasena ? "text" : "password"}
                    id="repetirContrasena"
                    value={repetirContrasena}
                    onChange={(e) => setRepetirContrasena(e.target.value)}
                    required
                    minLength="6"
                    maxLength="25"
                    className="pr-10"
                  />
                  <button
                    type="button"
                    onClick={() => toggleShowPassword("repetirContrasena")}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                  >
                    {showPassword.repetirContrasena ? (
                      <EyeOff className="h-4 w-4" />
                    ) : (
                      <Eye className="h-4 w-4" />
                    )}
                  </button>
                </div>
              </div>

              {error && <p className="text-center text-sm font-semibold text-destructive">{error}</p>}

              <Button type="submit" className="w-full">
                Crear cuenta
              </Button>
            </form>

            <p className="mt-6 text-center text-xs text-muted-foreground">
              Al continuar, aceptas las{" "}
              <Link className="text-primary hover:underline" to="/condiciones-uso">
                Condiciones de uso
              </Link>{" "}
              y el{" "}
              <Link className="text-primary hover:underline" to="/aviso-privacidad">
                Aviso de privacidad
              </Link>{" "}
              de Procu.
            </p>

            <p className="mt-4 text-center text-sm">
              ¿Ya tienes una cuenta?{" "}
              <Link to="/login" className="font-semibold text-primary hover:underline">
                Inicia sesión
              </Link>
            </p>
          </CardContent>
        </Card>
      </div>
    </PageLayout>
  );
};

export default Register;
